// webpack 빌드 시 필요한 클래스 import
import Utils from "./utils/Utils.js";

// 메인 게임 클래스
class Game {
  constructor() {
    // 코어 Three.js 컴포넌트
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.clock = new THREE.Clock();

    // 게임 시스템들
    this.initializer = null;
    this.gameLoop = null;
    this.debug = null;

    // 게임 컴포넌트들
    this.sceneManager = null;
    this.player = null;
    this.cameraController = null;
    this.inputHandler = null;
    this.uiManager = null; // UI 관리자

    // 게임 엔티티들
    this.itemManager = null; // 아이템 관리자 (모든 아이템 통합 관리)
    this.companionManager = null; // 동료 관리자

    // 새로운 적 스폰 시스템
    this.enemySpawnManager = null; // 적 스폰 관리자
    this.spawnScheduler = null; // 스폰 스케줄러

    // 콜리션 시스템
    this.collisionManager = null; // 콜리션 매니저

    // 관리자들
    this.stateManager = null; // 게임 상태 관리
    this.eventManager = null; // 게임 이벤트 관리
    this.coordinator = null; // 게임 시스템 조율
  }

  // 게임 초기화 - 이제 GameInitializer에 위임
  async init() {
    try {
      // import한 Utils 사용 (webpack 빌드에서는 import된 Utils, 개별 스크립트 로드 시에는 window.Utils)
      const UtilsClass = Utils || window.Utils;

      UtilsClass.log("Game.init() 시작");

      // 관리자들 먼저 초기화 (coordinator가 entitiesInitializer에서 필요함)
      // webpack 빌드에서는 전역 변수로 등록된 클래스 사용
      const GameStateManagerClass =
        typeof GameStateManager !== "undefined"
          ? GameStateManager
          : window.GameStateManager;
      const GameEventManagerClass =
        typeof GameEventManager !== "undefined"
          ? GameEventManager
          : window.GameEventManager;
      const GameCoordinatorClass =
        typeof GameCoordinator !== "undefined"
          ? GameCoordinator
          : window.GameCoordinator;
      const GameInitializerClass =
        typeof GameInitializer !== "undefined"
          ? GameInitializer
          : window.GameInitializer;
      const GameLoopClass =
        typeof GameLoop !== "undefined" ? GameLoop : window.GameLoop;
      const DebugClass = typeof Debug !== "undefined" ? Debug : window.Debug;

      UtilsClass.log("GameStateManager 생성 시작...");
      this.stateManager = new GameStateManagerClass(this);
      UtilsClass.log("GameStateManager 생성 완료");

      UtilsClass.log("GameEventManager 생성 시작...");
      this.eventManager = new GameEventManagerClass(this);
      UtilsClass.log("GameEventManager 생성 완료");

      UtilsClass.log("GameCoordinator 생성 시작...");
      this.coordinator = new GameCoordinatorClass(this);
      UtilsClass.log("GameCoordinator 생성 완료");

      // 초기화 관리자 생성 및 실행
      UtilsClass.log("GameInitializer 생성 시작...");
      this.initializer = new GameInitializerClass(this);
      UtilsClass.log("GameInitializer 생성 완료");

      UtilsClass.log("GameInitializer.initialize() 호출 시작...");
      await this.initializer.initialize();
      UtilsClass.log("GameInitializer.initialize() 완료");

      // CompanionManager와 GameStateManager 연결
      if (this.companionManager) {
        UtilsClass.log("CompanionManager와 GameStateManager 연결");
        this.companionManager.gameStateManager = this.stateManager;
      }

      // 게임 루프 생성 및 시작
      UtilsClass.log("GameLoop 생성 및 시작...");
      this.gameLoop = new GameLoopClass(this);
      this.gameLoop.start();
      UtilsClass.log("GameLoop 시작 완료");

      // 디버그 시스템 초기화
      UtilsClass.log("Debug 시스템 초기화...");
      this.debug = new DebugClass(this);
      UtilsClass.log("Debug 시스템 초기화 완료");

      UtilsClass.log("Game.init() 완료");
    } catch (error) {
      const UtilsClass = typeof Utils !== "undefined" ? Utils : window.Utils;
      if (UtilsClass) {
        UtilsClass.log("Game.init() 중 오류 발생: " + error.message, "error");
        UtilsClass.log("에러 스택: " + (error.stack || "스택 없음"), "error");
      }
      throw error;
    }
  }

  // 게임 일시정지
  pause(fromTab = false) {
    this.stateManager.pause(fromTab);
  }

  // 게임 재시작
  resume(fromTab = false) {
    this.stateManager.resume(fromTab);
  }

  // 게임 상태 확인
  getStatus() {
    return {
      isInitialized: !!(this.scene && this.camera && this.renderer),
      isRunning: this.gameLoop ? this.gameLoop.getStatus().isRunning : false,
      playerLoaded: this.player ? this.player.isModelLoaded() : false,
      enemyCount: this.enemySpawnManager
        ? this.enemySpawnManager.enemies.length
        : 0,
      playerPoints: this.player ? this.player.points : 0,
      itemStatus: this.itemManager ? this.itemManager.getStatus() : null,
    };
  }

  // 스폰 시스템 초기화
  startSpawnSystem() {
    this.coordinator.initSpawns();
  }

  // 스폰 스케줄러 정지
  stopSpawnSystem() {
    this.coordinator.stopSpawnSystem();
  }

  // 모든 적 제거
  removeAllEnemies() {
    this.coordinator.removeAllEnemies();
  }

  // 게임 상태 확인 (업데이트)
  getStatus() {
    const baseStatus = {
      isInitialized: !!(this.scene && this.camera && this.renderer),
      isRunning: this.gameLoop ? this.gameLoop.getStatus().isRunning : false,
      playerLoaded: this.player ? this.player.isModelLoaded() : false,
      enemyCount: this.enemySpawnManager
        ? this.enemySpawnManager.enemies.length
        : 0,
      playerPoints: this.player ? this.player.points : 0,
      itemStatus: this.itemManager ? this.itemManager.getStatus() : null,
    };

    // 새로운 적 스폰 시스템 상태 추가
    if (this.enemySpawnManager) {
      baseStatus.spawnSystemStatus = this.enemySpawnManager.getStatus();
    }

    if (this.spawnScheduler) {
      baseStatus.schedulerStatus = this.spawnScheduler.getStatus();
    }

    return baseStatus;
  }

  // 게임 종료 처리
  handleGameOver(attackerPosition = null) {
    this.stateManager.handleGameOver(attackerPosition);
  }

  // 게임 재시작
  restartGame() {
    this.stateManager.restartGame();
  }

  // 게임 종료 상태 확인
  isGameOverState() {
    return this.stateManager.isGameOverState();
  }

  // 동료 소환
  async spawnCompanion(type) {
    if (this.companionManager) {
      return await this.companionManager.spawnCompanion(type);
    }
    return false;
  }

  // 동료 제거
  removeCompanion(companion) {
    if (this.companionManager) {
      return this.companionManager.removeCompanion(companion);
    }
    return false;
  }

  // 모든 동료 제거
  removeAllCompanions() {
    if (this.companionManager) {
      this.companionManager.removeAllCompanions();
    }
  }

  // 동료 업그레이드
  upgradeCompanion(type, level) {
    if (this.companionManager) {
      return this.companionManager.upgradeCompanion(type, level);
    }
    return false;
  }

  // 동료 상태 정보 반환
  getCompanionStatus() {
    if (this.companionManager) {
      return this.companionManager.getCompanionStatus();
    }
    return [];
  }

  // 동료 정보 반환 (UI용)
  getCompanionInfo(type) {
    if (this.companionManager) {
      return this.companionManager.getCompanionInfo(type);
    }
    return null;
  }

  // 모든 동료 정보 반환
  getAllCompanionInfo() {
    if (this.companionManager) {
      return this.companionManager.getAllCompanionInfo();
    }
    return {};
  }

  // 동료 소환 가능 여부 체크
  canSpawnCompanion(type) {
    if (this.companionManager) {
      return this.companionManager.canSpawnCompanion(type);
    }
    return false;
  }
}

// 전역 변수로 등록 (개별 스크립트 로드 시에도 접근 가능하도록)
window.Game = Game;

// webpack 빌드를 위해 default export 추가
export default Game;

// 로드 확인 로그 (UI 로그 토글 반영)
try {
  const uiLogEnabled =
    typeof GameConfig !== "undefined" &&
    GameConfig.DEBUG &&
    GameConfig.DEBUG.UI_LOG_ENABLED === true;
  if (uiLogEnabled && typeof window.simpleLog === "function") {
    window.simpleLog("Game.js 로드 완료 및 전역 등록 완료", "info");
  }
} catch (_) {}
